// material
import { styled } from '@mui/system';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import Grid from '@mui/material/Grid2';
import get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import { TCustomTheme } from '@mgm/common/theme';

import { useResponsive } from '@mgm/common/hooks/useResponsive';
import Chart, { useChart } from '@mgm/common/components/charts';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { useAppSelector } from '../../../hooks/useAppStore';
import { getDashboardData, getPreset } from '../../../store/dashboard/selectors';
import { getShopifyIdToMemberMap } from '../../../store/team/selectors';
import { getCurrencyByCode } from '../../../store/common/selectors';
import { TDashboardOrders } from '../../../types';

const StyledRoot = styled(Card)(({ theme }) => ({
  '& .apexcharts-legend': {
    width: 240,
    margin: 'auto',
    [theme.breakpoints.up('sm')]: {
      flexWrap: 'wrap',
      height: 160,
      width: '50%',
    },
  },
  '& .apexcharts-datalabels-group': {
    display: 'none',
  },
}));

const DashboardSalesByEmployees = () => {
  const { t } = useTranslation(['dashboard', 'common', 'timesheets']);
  const theme = useTheme() as TCustomTheme;
  const { orders } = useAppSelector(getDashboardData);
  const chartRef = useRef<any>(null);
  const isChartMounted = useRef(false);
  const preset = useAppSelector(getPreset);

  const isDesktop = useResponsive('up', 'sm');

  const getFirstKey = (obj: TDashboardOrders['salesByEmployeePizzaGraph']) => {
    const keys = Object.keys(obj);
    return keys?.[0] || [];
  };

  const shopifyIdToMemberMap = useAppSelector(getShopifyIdToMemberMap);
  const [selectedEmployee, setSelectedEmployee] = useState(getFirstKey(orders?.salesByEmployeePizzaGraph));
  const getCurrency = useAppSelector(getCurrencyByCode);
  const [isChartReady, setIsChartReady] = useState(false);

  useEffect(() => {
    setSelectedEmployee(getFirstKey(orders?.salesByEmployeePizzaGraph));
  }, [orders]);

  const labels: Array<string> = get(orders?.salesByEmployeePizzaGraph, `${selectedEmployee}.labels`, [])?.map((label) => shopifyIdToMemberMap[label]?.name || label);

  useEffect(() => {
    if (isChartReady && chartRef.current?.updateOptions) {
      try {
        chartRef.current?.updateOptions({
          labels,
        }, true, true);
      } catch (error) {
        console.error('Failed to update chart options:', error);
      }
    }
  }, [isChartReady, labels]);

  const chartOptions = useChart({
    chart: {
      events: {
        mounted: (chart: any) => {
          isChartMounted.current = true;
          chartRef.current = chart;
          setIsChartReady(true);
        },
      },
    },
    labels,
    stroke: {
      colors: [theme.palette.background.paper],
    },
    tooltip: {
      enabled: true,
      fillSeriesColor: false,
      y: {
        formatter(val: number) {
          return `${val.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} ${getCurrency().mark}`;
        },
      },
    },
    fill: { opacity: 0.8 },
    legend: {
      position: 'right',
      itemMargin: {
        horizontal: 10,
        vertical: 5,
      },
    },
    responsive: [
      {
        breakpoint: theme.breakpoints.values.sm,
        options: {
          legend: {
            position: 'bottom',
            horizontalAlign: 'left',
          },
        },
      },
    ],
  });

  const getTotalAmount = useMemo(() => {
    const values = get(orders?.salesByEmployeePizzaGraph, `${selectedEmployee}.values`) || [];
    const value = values?.reduce((a: number, b: number) => a + b, 0);

    return `${value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${getCurrency().mark}`;
  }, [orders, selectedEmployee, preset]);

  return (
    <StyledRoot>
      <Grid container>
        <Grid size={{
          lg: 6,
          md: 6,
          xs: 12,
          sm: 6,
        }}
        >
          <CardHeader
            title={t('salesByEmployee')}
            titleTypographyProps={{
              variant: 'h5',
            }}
          />
        </Grid>
      </Grid>
      <Box sx={{ my: 5 }} dir="ltr">
        {!labels.includes('No Data') && labels?.length > 0 ? (
          <Chart
            type="polarArea"
            series={[...get(orders?.salesByEmployeePizzaGraph, `${selectedEmployee}.values`, [])]}
            options={chartOptions}
            height={isDesktop ? 260 : 360}
          />
        ) : (
          <Box height={350}>
            <CustomNoRowsOverlay text={t('looksLikeTheresNoDataForTheSelectedMonth', { ns: 'common' })} />
          </Box>
        ) }
      </Box>

      <Divider sx={{ borderStyle: 'dashed' }} />

      {!labels.includes('No Data') && labels?.length > 0 && (
        <Stack
          direction="row"
          divider={(
            <Divider
              orientation="vertical"
              sx={{
                borderStyle: 'dashed',
              }}
              flexItem
            />
)}
        >
          <Box sx={{ py: 2, width: 1, textAlign: 'center' }}>
            <Typography sx={{ mb: 1, typography: 'body2', color: 'text.secondary' }}>{t('employees', { ns: 'common' })}</Typography>
            <Typography sx={{ typography: 'h4' }}>{get(orders?.salesByEmployeePizzaGraph, `${selectedEmployee}.labels.length`)}</Typography>
          </Box>

          <Box sx={{ py: 2, width: 1, textAlign: 'center' }}>
            <Typography sx={{ mb: 1, typography: 'body2', color: 'text.secondary' }}>{t('amount', { ns: 'timesheets' })}</Typography>
            <Typography sx={{ typography: 'h4' }}>
              {
                getTotalAmount
              }
            </Typography>
          </Box>
        </Stack>
      )}
    </StyledRoot>
  );
};

export {
  DashboardSalesByEmployees,
};
